<template>
  <div class="chatter-feed-event">
    <div class="chatter-feed-event__avatar" aria-hidden="true">
      <img v-if="avatarPicture" :src="avatarPicture" alt="" />
      <span v-else class="chatter-feed-event__initials">{{ initials }}</span>
    </div>
    <div class="chatter-feed-event__content">
      <div class="chatter-feed-event__top-row">
        <span class="chatter-feed-event__actor">{{ actorName }}</span>
        <span class="chatter-feed-event__time">{{ relativeTime }}</span>
        <span
          v-if="showBadge"
          class="chatter-feed-event__badge"
          data-testid="chatter-feed-event-history-badge"
        >
          History
        </span>
      </div>
      <div class="chatter-feed-event__action">{{ action }}</div>
    </div>
  </div>
</template>

<script setup>
import { computed } from "vue";

const props = defineProps({
  actor: {
    type: Object,
    default: () => ({}),
  },
  relativeTime: {
    type: String,
    default: "",
  },
  action: {
    type: String,
    default: "",
  },
  /** Hide the "History" badge (e.g. when the History filter is active). */
  showBadge: {
    type: Boolean,
    default: true,
  },
});

const avatarPicture = computed(() => {
  const picture = props.actor?.picture;
  if (picture && typeof picture === "object" && picture.default) return picture.default;
  return picture || "";
});

const actorName = computed(() => {
  const first = (props.actor?.firstName || "").trim();
  const last = (props.actor?.lastName || "").trim();
  return [first, last].filter(Boolean).join(" ") || "—";
});

const initials = computed(() => {
  const first = (props.actor?.firstName || "").trim();
  const last = (props.actor?.lastName || "").trim();
  const f = first ? first[0] : "";
  const l = last ? last[0] : "";
  return (f + l).toUpperCase() || "?";
});
</script>

<style scoped>
.chatter-feed-event {
  display: flex;
  gap: 12px;
  width: 552px;
  min-height: 44px;
}

.chatter-feed-event__avatar {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 24px;
  height: 24px;
  border-radius: 9999px;
  background: #f2f4f7;
  border: 0.5px solid rgba(0, 0, 0, 0.08);
  overflow: hidden;
}

.chatter-feed-event__avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.chatter-feed-event__initials {
  font-family: Inter;
  font-size: 10px;
  font-weight: 500;
  color: #475467;
}

.chatter-feed-event__content {
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex: 1;
}

.chatter-feed-event__top-row {
  display: flex;
  align-items: center;
  gap: 8px;
  min-height: 22px;
}

.chatter-feed-event__actor {
  font-family: Inter;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  color: #344054;
}

.chatter-feed-event__time {
  font-family: Inter;
  font-size: 12px;
  font-weight: 400;
  line-height: 18px;
  color: #667085;
}

.chatter-feed-event__badge {
  margin-left: auto;
  padding: 2px 8px;
  border-radius: 9999px;
  background: #f9fafb;
  border: 1px solid #eaecf0;
  font-family: Inter;
  font-size: 12px;
  font-weight: 500;
  line-height: 18px;
  color: #344054;
}

.chatter-feed-event__action {
  font-family: Inter;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  color: #475467;
  white-space: pre-wrap;
  word-break: break-word;
}
</style>
